import {ObservabilityMetrics} from 'src/utils/observability/metrics';

import type {
  GaugeEvent,
  IncrementEvent,
  MetricTags,
  Metrics,
  SetEvent,
} from 'src/metrics';

export const OBSERVABILITY_CONFIG = {
  service: 'sail-comms', // client_id in the AEL client config
  project: 'secure_frameworks',
  analytics: {
    parametersAllowList: ['log_level', 'log_message'],
  },
  batchSettings: {
    batchTimeout: 1000,
  },
};

/**
 * Use cases for Comms metrics.
 *
 * Before adding to this list, ensure that the usecase you're adding is present on [this list](https://git.corp.stripe.com/stripe-internal/zoolander/blob/ab08bf5167d44ef39bbaed48c26689fc46107e4c/src/resources/com/stripe/dscore/analyticseventlogger/server/rpcserver/client_config.yaml#L4127)
 * and `analytics-event-logger` is deployed on the commit. Otherwise the tag will not be included in metrics:
 */
export const USE_CASES = {
  UNKNOWN: 'unknown',
  TEST_SECURE_FRAMEWORKS: 'test_secure_frameworks',
  STRIPE_THIRD_PARTY: 'stripethirdparty',
  ACF: 'acf',
} as const;

/**
 * The Usecase type represents the use case for sail/comms metrics.
 * It can be one of the predefined use cases in USE_CASES.
 */
export type Usecase = (typeof USE_CASES)[keyof typeof USE_CASES];

/**
 * CommsObservabilityMetrics is an implementation of the Metrics interface that sends metrics via [AEL](https://go/ael).
 *
 * It requires adding https://r.stripe.com to the `connect-src` of your [service's CSP configuration](https://go/wsp/csp).
 *
 * Also if your service is not hosted on a subdomain of stripe.com, b.stripecdn.com or dev.stripe.me, you will need to add your domain to the [client_config.yaml](https://git.corp.stripe.com/stripe-internal/zoolander/blob/bc6d6807f0c4c0db192e6e088af6651849d56c95/src/resources/com/stripe/dscore/analyticseventlogger/server/rpcserver/client_config.yaml#L4159)
 */
export class CommsObservabilityMetrics implements Metrics {
  client: ObservabilityMetrics;

  constructor(private usecase: Usecase = USE_CASES.UNKNOWN) {
    this.client = new ObservabilityMetrics(OBSERVABILITY_CONFIG);
  }

  gauge(event: GaugeEvent, duration: number, tags: MetricTags = {}): void {
    this.client.gauge(event, {duration}, this.withGlobalTags(tags));
  }

  increment(event: IncrementEvent, tags: MetricTags = {}): void {
    this.client.increment(event, this.withGlobalTags(tags));
  }

  set(
    event: SetEvent,
    params: Record<string, string | number | boolean>,
    tags: MetricTags = {},
  ): void {
    this.client.set(event, params, this.withGlobalTags(tags));
  }

  private withGlobalTags(tags: MetricTags): MetricTags {
    return {
      ...tags,
      use_case: this.usecase,
    };
  }
}
